/**
 * Dialog — diálogo modal único del CMDB (confirmaciones, copias, mover…).
 *
 * Usa las clases `.list-overlay` / `.list-dialog` / `.list-dialog-actions` de
 * `styles/lists.css`, que ya resuelven: overlay con blur, superficie OPACA
 * (`--bg-surface`, importante en los skins Lumina, donde `--bg-card` es
 * translúcido), Esc para cerrar y bloqueo del scroll del cuerpo.
 *
 * SE PINTA EN `document.body` (portal)
 * ---------------------------------------------------------------------------
 * El overlay es `position: fixed`, pero cualquier ancestro con `transform`,
 * `filter` o `backdrop-filter` lo convierte en «absoluto respecto a ese
 * ancestro»: el diálogo aparecía dentro del área de contenido, desplazado a la
 * izquierda y por DEBAJO del menú lateral y de la barra superior. Con el portal
 * se cuelga del `body` y el `inset: 0` es la ventana entera. Los tokens de los
 * skins están en `<html>`, así que se siguen heredando.
 *
 * El contenido (título, cuerpo y botones) lo pone quien lo usa.
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

export default function Dialog({
  title,
  icon,
  children,
  footer,
  onClose,
  maxWidth = 420,
  busy = false,
  className = '',
}) {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) onClose?.();
    };
    document.body.classList.add('modal-open');
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove('modal-open');
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, busy]);

  const contenido = (
    <div className="list-overlay" onClick={() => !busy && onClose?.()}>
      <div
        className={`list-dialog ${className}`.trim()}
        style={{ maxWidth }}
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        {title && (
          <h4>
            {icon && (
              <span className="list-dialog-icon" aria-hidden="true">{icon}</span>
            )}
            {title}
          </h4>
        )}
        {/* El cuerpo va aparte del título y de los botones para que, cuando el
            contenido es largo, el diálogo haga scroll DENTRO y las acciones
            sigan siempre a la vista. */}
        <div className="list-dialog-body">{children}</div>
        {footer && <div className="list-dialog-actions">{footer}</div>}
      </div>
    </div>
  );

  // Si el diálogo se pinta en el servidor (no es el caso hoy) no hay `document`.
  if (typeof document === 'undefined') return contenido;
  // OJO: con un elemento en pantalla completa (el visor de rack tiene su modo
  // de trabajo), lo que se cuelga de `document.body` queda FUERA de la capa
  // superior del navegador y no se ve. El portal tiene que ir al elemento en
  // pantalla completa cuando lo haya.
  return createPortal(contenido, document.fullscreenElement || document.body);
}
